import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import getChartColorsArray from "Components/Common/ChartsDynamicColor";
import { getPaymentsGenderDistribution } from "common/services/payments.service";
import * as moment from "moment";
import useQueryState from "Components/Hooks/UseQueryState";
import { FilterType, defaultFilter } from "../Filters";

const convertGenderData = (data: {
  [key: string]: { count: number; totalAmountPerMonth: number };
}) => {
  // Extract property names into a labels array
  const labels = Object.keys(data);

  // Extract counts into a series array
  const countSeries = labels.map((key) => data[key].count);

  // Extract totalAmountPerMonth into another series array
  const totalAmountPerMonthSeries = labels.map(
    (key) => data[key].totalAmountPerMonth
  );

  return { labels, countSeries, totalAmountPerMonthSeries };
};

const AmountDistributionChart = ({
  data,
  labels,
}: {
  data: number[];
  labels: string[];
}) => {
  const chartPieBasicColors = getChartColorsArray(
    '["#1C92D8", "#F06C6B", "#CCCCCC"]'
  );

  const options = {
    chart: {
      height: 450,
      type: "pie",
    },
    labels: labels,
    legend: {
      position: "top",
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
    },
    colors: chartPieBasicColors,
    tooltip: {
      enabled: true, // Enable tooltips
      y: {
        formatter: function (value: number) {
          return "$ " + value.toLocaleString(); // Format the tooltip value
        },
        title: {
          formatter: function (seriesName: string) {
            return `${seriesName}:`; // Customize the title of the tooltip
          },
        },
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={data}
      options={options}
      type="pie"
      height={300}
    />
  );
};

const ContractDistributionChart = ({
  data,
  labels,
}: {
  data: number[];
  labels: string[];
}) => {
  const chartPieBasicColors = getChartColorsArray(
    '["#1C92D8", "#F06C6B", "#CCCCCC"]'
  );

  const options = {
    chart: {
      height: 450,
      type: "pie",
    },
    labels: labels,
    legend: {
      position: "top",
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
    },
    colors: chartPieBasicColors,
    tooltip: {
      enabled: true, // Enable tooltips
      y: {
        formatter: function (value: number) {
          return value.toLocaleString() + " Contracts"; // Format the tooltip value
        },
        title: {
          formatter: function (seriesName: string) {
            return `${seriesName}:`; // Customize the title of the tooltip
          },
        },
      },
    },
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={data}
      options={options}
      type="pie"
      height={300}
    />
  );
};

const GenderDistribution = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState({});
  const [, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsGenderDistribution({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.region?.value,
    filter?.paymentCategory?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
  ]);

  const [activeTab, setActiveTab] = useState("number");

  const { labels, countSeries, totalAmountPerMonthSeries } =
    convertGenderData(payments);

  return (
    <Card className="card-height-100">
      <CardHeader className=" align-items-center d-flex">
        <div className="flex-grow-1">
          <h4 className="card-title mb-0 ">Contract Distribution by Gender</h4>
          {/* <p className="text-muted mb-0">
            Visual breakdown of contract allocations based on gender categories:
            Male, Female, and Unspecified.
          </p> */}
        </div>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "number" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("number")}
          >
            NUMBER
          </button>
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "amount" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("amount")}
          >
            AMOUNT
          </button>
        </div>
      </CardHeader>

      <CardBody>
        {activeTab === "number" ? (
          <ContractDistributionChart data={countSeries} labels={labels} />
        ) : (
          <AmountDistributionChart
            data={totalAmountPerMonthSeries}
            labels={labels}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default GenderDistribution;
